import { AbstractService } from '@/services/core/serviceFactory';
import { usersGraphqlService, type EnrollmentSpecialist as APIEnrollmentSpecialist } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';

export interface EnrollmentSpecialist {
  hbox_user_id: number;
  es_name: string;
}

export interface EnrollmentSpecialistsResponse {
  pep_vw_enrollment_specialists: APIEnrollmentSpecialist[];
}

export class EnrollmentSpecialistsService extends AbstractService {
  /**
   * Fetch all enrollment specialists
   */
  async fetchEnrollmentSpecialists(): Promise<GraphQLClientResponse<EnrollmentSpecialistsResponse>> {
    this.requireAuth();

    try {
      return await usersGraphqlService.getEnrollmentSpecialists(
        this.context.hasuraToken
      ) as GraphQLClientResponse<EnrollmentSpecialistsResponse>;
    } catch (error) {
      console.error('Error fetching enrollment specialists:', error);
      throw new Error('Failed to fetch enrollment specialists');
    }
  }

  /**
   * Filter enrollment specialists by name
   */
  filterSpecialists(specialists: EnrollmentSpecialist[], searchTerm: string): EnrollmentSpecialist[] {
    if (!searchTerm) return specialists;

    const searchTermLower = searchTerm.toLowerCase();
    
    return specialists.filter((specialist: EnrollmentSpecialist) => 
      specialist.es_name?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Sort enrollment specialists by name
   */
  sortSpecialists(specialists: EnrollmentSpecialist[], sortOrder: 'asc' | 'desc' = 'asc'): EnrollmentSpecialist[] {
    return [...specialists].sort((a, b) => {
      const aName = a.es_name || '';
      const bName = b.es_name || '';
      
      return sortOrder === 'asc' 
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    });
  }

  /**
   * Find enrollment specialist by ID
   */
  findSpecialistById(specialists: EnrollmentSpecialist[], id: number): EnrollmentSpecialist | undefined {
    return specialists.find(specialist => specialist.hbox_user_id === id);
  }

  /**
   * Get enrollment specialist names as options for dropdowns
   */
  getSpecialistOptions(specialists: EnrollmentSpecialist[]): Array<{ value: number; label: string }> {
    return specialists.map(specialist => ({
      value: specialist.hbox_user_id,
      label: specialist.es_name || `User ${specialist.hbox_user_id}`
    }));
  }
}